import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, ListChecks, Lock } from "lucide-react";
import api from "../api";
import i18n from "../i18n";
import { Badge, Spinner } from "./common";
import TranscriptDropzone from "./TranscriptDropzone";

/**
 * A deal's (or project's) meetings, with its own drop zone. The second entry
 * point for transcript ingestion alongside the /meetings page: a meeting belongs
 * to a deal most of the time, and this is where people already are when they
 * have a transcript in hand.
 *
 * Renders nothing for a guest -- the backend blocks them from reading meeting
 * content, so the card would only ever show an error.
 */
export default function MeetingsCard({ dealId, projectId, writable }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/meetings", { params: { deal_id: dealId || "", project_id: projectId || "", limit: 5 } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [dealId, projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="border border-border rounded-sm p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold flex items-center gap-2">
          <CalendarClock size={16} className="text-glow" />{t("meet.dealCardTitle")}
        </h3>
        <Link to="/meetings" className="text-xs text-muted hover:text-txt transition-colors">
          {t("meet.seeAll")}
        </Link>
      </div>

      {items === null ? <Spinner /> : items.length === 0 ? (
        <p className="text-sm text-muted">{t("meet.noneForDeal")}</p>
      ) : (
        <div className="space-y-2" data-testid="deal-meetings">
          {items.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/meetings/${m.id}`)}
              className="w-full text-left flex items-start gap-3 py-1.5 hover:opacity-80 transition-opacity"
            >
              <span className="mt-1.5 w-2 h-2 rounded-full bg-glow shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm flex items-center gap-2 flex-wrap">
                  <span className="truncate">{m.subject}</span>
                  {m.sensitive && <Lock size={12} className="text-amber-600 shrink-0" />}
                  <Badge value={m.state} label={t(`meet.state.${m.state}`)} />
                </div>
                <div className="text-xs text-muted flex items-center gap-2 flex-wrap">
                  <span>
                    {new Date(m.starts_at || m.created_at).toLocaleDateString(i18n.language, {
                      month: "short", day: "numeric",
                    })}
                  </span>
                  {m.open_action_items > 0 && (
                    <span className="inline-flex items-center gap-1 text-primary">
                      <ListChecks size={11} /> {t("meet.openItems", { count: m.open_action_items })}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {writable && (
        <TranscriptDropzone
          dealId={dealId}
          projectId={projectId}
          compact
          onUploaded={(m) => navigate(`/meetings/${m.id}`)}
        />
      )}
    </div>
  );
}
